import { Link } from "@tanstack/react-router";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export function TermsAndConditionsPage() {
  useDocumentTitle("Terms and conditions");
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/login"
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          ← Back
        </Link>

        <header className="mt-6 space-y-1">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            Boo Lab, Inc.
          </p>
          <h1 className="text-3xl font-semibold">Terms and Conditions</h1>
          <p className="text-sm text-muted-foreground">Effective Date: April 29, 2026</p>
        </header>

        <div className="mt-8 space-y-4 text-sm leading-6 text-foreground">
          <p>
            These Terms and Conditions govern access to and use of AI Enterprise Agents, a
            business software service provided by Boo Lab, Inc.
          </p>
          <p>
            By accessing or using the Services, creating an account, signing an order form, or
            otherwise agreeing to these Terms, the Customer agrees to these Terms. If an
            individual accepts these Terms on behalf of a company or other organization, that
            individual represents that they have authority to bind that organization.
          </p>
          <p>
            The Services are for business use only and are not intended for personal, household,
            or consumer use.
          </p>

          <h2 className="mt-10 text-lg font-semibold">1. Definitions</h2>
          <p>"Boo Lab," "we," "us," and "our" mean Boo Lab, Inc.</p>
          <p>
            "Customer" means the company or organization that accesses, purchases, subscribes
            to, evaluates, or uses the Services.
          </p>
          <p>"User" means an individual authorized by Customer to access or use the Services.</p>
          <p>
            "Services" means AI Enterprise Agents, https://boolab.ai, related software, agents,
            APIs, workflows, integrations, documentation, support, and other services provided by
            Boo Lab.
          </p>
          <p>
            "Customer Content" means data, prompts, instructions, files, messages, records,
            outputs, workflows, metadata, or other content submitted to, processed by, or
            generated through the Services on behalf of Customer.
          </p>
          <p>
            "Order" means an order form, online checkout, subscription plan, statement of work,
            or other ordering document accepted by Boo Lab.
          </p>
          <p>
            "Third Party Services" means third party applications, platforms, APIs, models, data
            sources, tools, infrastructure, and integrations used with or connected to the
            Services.
          </p>

          <h2 className="mt-10 text-lg font-semibold">2. Services</h2>
          <p>
            Boo Lab provides AI Enterprise Agents to help Customers configure, deploy, and
            manage AI agents that can work across business tools, communications systems,
            documents, software repositories, databases, workflows, and other Customer selected
            systems.
          </p>
          <p>
            The Services may include capabilities for retrieval, summarization, drafting,
            classification, reasoning, planning, workflow automation, tool use, code generation,
            task execution, message generation, ticket creation, record updates, reporting,
            monitoring, and similar functions.
          </p>
          <p>
            Boo Lab may update, modify, improve, suspend, or discontinue parts of the Services
            from time to time, provided that Boo Lab will not materially reduce the core
            functionality of a paid subscription during its term without reasonable notice or a
            reasonable alternative.
          </p>

          <h2 className="mt-10 text-lg font-semibold">3. Eligibility and Business Use</h2>
          <p>
            The Services may only be used by business customers and Users who are at least 18
            years old.
          </p>
          <p>
            Customer may not allow personal, household, or consumer use of the Services.
          </p>

          <h2 className="mt-10 text-lg font-semibold">4. Accounts and Administration</h2>
          <p>
            Customer is responsible for all activity under its accounts, workspaces, API keys,
            credentials, integrations, and Users.
          </p>
          <p>
            Customer must keep login credentials, API keys, tokens, and secrets secure. Customer
            must promptly notify Boo Lab at legal@boolab.ai of any unauthorized access,
            credential compromise, or security incident involving the Services.
          </p>
          <p>
            Customer is responsible for managing User permissions, admin roles, integration
            scopes, approval settings, and agent access to systems and data.
          </p>

          <h2 className="mt-10 text-lg font-semibold">5. Customer Content</h2>
          <p>
            Customer owns Customer Content. As between Customer and Boo Lab, Customer retains
            all rights in Customer Content.
          </p>
          <p>
            Customer grants Boo Lab the rights needed to host, process, transmit, display,
            generate, analyze, transform, store, copy, and otherwise use Customer Content to
            provide, secure, support, and improve the Services as described in these Terms and
            the Privacy Policy.
          </p>
          <p>
            Customer represents and warrants that it has all rights, permissions, notices, and
            consents necessary to provide Customer Content to Boo Lab and to allow Boo Lab to
            process Customer Content as contemplated by these Terms.
          </p>
          <p>
            Customer is responsible for the accuracy, quality, legality, and appropriateness of
            Customer Content and for all decisions made based on Customer Content or agent
            outputs.
          </p>

          <h2 className="mt-10 text-lg font-semibold">
            6. Use of Customer Content for Product and Model Improvement
          </h2>
          <p>
            Unless Customer opts out, Boo Lab may use Customer Content, prompts, agent
            interactions, outputs, telemetry, feedback, and usage data to develop, evaluate,
            train, fine tune, test, secure, and improve Boo Lab models, agents, products, and
            services.
          </p>
          <p>
            Customer may opt out of broader Boo Lab model improvement by contacting
            privacy@boolab.ai or through any product controls Boo Lab makes available.
          </p>
          <p>
            After an opt out is processed, Boo Lab will not use Customer Content for broader Boo
            Lab model training or improvement, except where needed to provide the Services,
            maintain security, debug issues, comply with law, enforce agreements, or as otherwise
            permitted by Customer.
          </p>
          <p>
            Boo Lab may use aggregated, deidentified, anonymized, or statistical information for
            analytics, benchmarking, security, reliability, and product development, provided it
            does not identify Customer or any individual.
          </p>
          <p>
            Customer specific configuration, evaluation, retrieval, memory, or improvement
            features may continue to operate when enabled by Customer, because those features
            are part of providing the Services to Customer.
          </p>

          <h2 className="mt-10 text-lg font-semibold">
            7. AI Outputs and Customer Responsibility
          </h2>
          <p>
            The Services may generate outputs that are inaccurate, incomplete, inappropriate,
            duplicative, or unsuitable for Customer's intended use.
          </p>
          <p>
            Customer is responsible for reviewing, validating, and approving outputs before
            relying on them, publishing them, sending them, or using them for legal, financial,
            employment, medical, safety, regulated, or similarly significant decisions.
          </p>
          <p>
            Boo Lab does not guarantee that outputs will be accurate, error free, non
            infringing, secure, compliant, or appropriate for any particular use.
          </p>
          <p>
            Customer is responsible for configuring approval flows and human review where agent
            actions could affect third parties, business records, finances, communications, legal
            obligations, employment, customer relationships, security, or production systems.
          </p>

          <h2 className="mt-10 text-lg font-semibold">8. Agent Actions and Integrations</h2>
          <p>
            Customer may configure AI Enterprise Agents to access, retrieve, write, update,
            send, post, delete, create, or otherwise act within connected systems.
          </p>
          <p>
            Integrations may include Slack, Microsoft Teams, Discord, Gmail, Google Calendar,
            Google Drive, Google Docs, Google Sheets, Google Slides, GitHub, GitLab, Bitbucket,
            Jira, Confluence, Notion, Linear, Asana, Trello, Salesforce, HubSpot, Zendesk,
            Intercom, Stripe, Airtable, Box, Dropbox, OneDrive, SharePoint, Zoom, Microsoft
            Outlook, Microsoft Exchange, AWS, Azure, Google Cloud, Datadog, Snowflake, BigQuery,
            PostgreSQL, MySQL, MongoDB, Redis, Elasticsearch, Segment, Amplitude, Mixpanel,
            Zapier, Make, Workato, Okta, Auth0, and other business systems selected by Customer.
          </p>
          <p>
            Customer is solely responsible for enabling integrations, granting permissions,
            configuring scopes, maintaining third party accounts, complying with Third Party
            Service terms, and monitoring agent actions in connected systems.
          </p>
          <p>
            Boo Lab is not responsible for Third Party Services, changes to Third Party
            Services, outages of Third Party Services, or actions taken by Third Party Services.
          </p>

          <h2 className="mt-10 text-lg font-semibold">9. Acceptable Use</h2>
          <p>Customer and Users may not use the Services to:</p>
          <ol className="list-decimal space-y-1 pl-6">
            <li>violate law or third party rights;</li>
            <li>upload or process content that Customer does not have the right to use;</li>
            <li>transmit malware or malicious code;</li>
            <li>compromise, disrupt, overload, or interfere with the Services;</li>
            <li>bypass security, access controls, usage limits, or rate limits;</li>
            <li>
              reverse engineer or attempt to derive source code, models, model weights, system
              prompts, or underlying non public technology except where permitted by law;
            </li>
            <li>
              use the Services to build or train a competing product using non public Boo Lab
              technology;
            </li>
            <li>
              scrape, crawl, or extract data from the Services except through documented APIs or
              approved export features;
            </li>
            <li>
              use the Services for biometric identification, unlawful surveillance, social
              scoring, deceptive impersonation, weapons development, or other harmful or unlawful
              purposes;
            </li>
            <li>
              use the Services to make automated decisions with legal or similarly significant
              effects without appropriate human review and legal compliance;
            </li>
            <li>
              submit highly sensitive information unless authorized under Customer's agreement
              with Boo Lab and configured with appropriate safeguards;
            </li>
            <li>remove, obscure, or alter proprietary notices.</li>
          </ol>
          <p>
            Boo Lab may suspend access to the Services if Boo Lab reasonably believes Customer
            or Users have violated these Terms, created a security risk, caused harm to the
            Services or others, or used the Services unlawfully.
          </p>

          <h2 className="mt-10 text-lg font-semibold">10. Fees and Payment</h2>
          <p>Customer must pay all fees stated in the applicable Order.</p>
          <p>
            Unless an Order states otherwise, fees are due according to the billing terms shown
            in the Order or invoice and are non refundable except as expressly stated in these
            Terms or required by law.
          </p>
          <p>
            Customer is responsible for taxes, duties, and similar governmental assessments,
            other than taxes based on Boo Lab's net income.
          </p>
          <p>
            If Customer does not pay amounts when due, Boo Lab may suspend or terminate access
            after reasonable notice, charge late fees where permitted by law, and recover
            reasonable collection costs.
          </p>

          <h2 className="mt-10 text-lg font-semibold">11. Trials and Beta Services</h2>
          <p>
            Boo Lab may offer trials, previews, pilots, beta features, experimental agents, or
            early access features. These are provided for evaluation and may be modified,
            limited, or discontinued at any time.
          </p>
          <p>
            Trials and beta services are provided as is and without warranties to the fullest
            extent permitted by law.
          </p>

          <h2 className="mt-10 text-lg font-semibold">12. Confidentiality</h2>
          <p>
            Each party may receive confidential or proprietary information from the other party.
          </p>
          <p>
            The receiving party must use the disclosing party's confidential information only to
            perform under these Terms, protect it using reasonable care, and not disclose it
            except to personnel, service providers, advisors, or contractors who need to know it
            and are bound by confidentiality obligations.
          </p>
          <p>
            Confidential information does not include information that is publicly available
            without breach, already known without restriction, independently developed without
            use of the other party's confidential information, or lawfully received from a third
            party without restriction.
          </p>
          <p>
            The receiving party may disclose confidential information if required by law,
            provided it gives reasonable notice where legally permitted.
          </p>

          <h2 className="mt-10 text-lg font-semibold">13. Security</h2>
          <p>
            Boo Lab will maintain reasonable administrative, technical, and organizational
            safeguards designed to protect Customer Content.
          </p>
          <p>
            Customer is responsible for secure configuration of accounts, Users, permissions,
            integrations, approval flows, secrets, credentials, endpoints, and Customer systems.
          </p>
          <p>
            Customer may not perform penetration testing, vulnerability scanning, load testing,
            or security assessment of the Services without prior written approval from Boo Lab.
          </p>

          <h2 className="mt-10 text-lg font-semibold">14. Privacy and Data Processing</h2>
          <p>
            Boo Lab's Privacy Policy is available at https://boolab.ai and is incorporated into
            these Terms.
          </p>
          <p>
            Where required, the parties may enter into a data processing addendum or similar
            agreement governing the processing of personal information in Customer Content.
          </p>
          <p>
            Customer acknowledges that Boo Lab may use service providers and subprocessors to
            provide the Services, including cloud infrastructure providers, AI model providers,
            security providers, analytics providers, support providers, and communication
            providers.
          </p>

          <h2 className="mt-10 text-lg font-semibold">15. Intellectual Property</h2>
          <p>
            Boo Lab and its licensors own all rights in the Services, software, agents, models,
            APIs, documentation, designs, systems, technology, workflows, templates,
            improvements, and related intellectual property.
          </p>
          <p>
            Subject to Customer's compliance with these Terms and payment of applicable fees,
            Boo Lab grants Customer a limited, non exclusive, non transferable right to access
            and use the Services during the applicable subscription term for Customer's internal
            business purposes.
          </p>
          <p>No rights are granted except as expressly stated in these Terms.</p>
          <p>
            Customer may provide feedback, suggestions, ideas, or recommendations. Boo Lab may
            use feedback without restriction or obligation.
          </p>

          <h2 className="mt-10 text-lg font-semibold">16. Output Rights</h2>
          <p>
            Subject to these Terms and Customer's payment obligations, Customer may use outputs
            generated by the Services for Customer's internal business purposes.
          </p>
          <p>
            Because AI systems may generate similar or identical outputs for different
            customers, Boo Lab does not guarantee exclusivity in outputs.
          </p>
          <p>
            Customer is responsible for determining whether outputs may be legally used,
            protected, published, registered, or enforced.
          </p>

          <h2 className="mt-10 text-lg font-semibold">17. Third Party Services and Models</h2>
          <p>
            The Services may use or connect to Third Party Services, including AI model
            providers, cloud providers, business applications, communication platforms, data
            sources, and integration platforms.
          </p>
          <p>
            Third Party Services are governed by their own terms and policies. Boo Lab does not
            control Third Party Services and is not responsible for their acts, omissions,
            availability, security, or compliance.
          </p>
          <p>
            Customer authorizes Boo Lab to transmit Customer Content to Third Party Services as
            necessary to provide the Services and operate Customer selected integrations.
          </p>

          <h2 className="mt-10 text-lg font-semibold">18. Compliance with Laws</h2>
          <p>Each party must comply with laws applicable to its performance under these Terms.</p>
          <p>
            Customer is responsible for its use of the Services, Customer Content, industry
            specific obligations, user notices, consents, employment laws, privacy laws, AI
            governance obligations, export laws, sanctions laws, and legal review of agent use
            cases.
          </p>
          <p>
            Customer may not use the Services in or for the benefit of countries, entities, or
            persons subject to applicable sanctions or export restrictions.
          </p>

          <h2 className="mt-10 text-lg font-semibold">19. Term and Termination</h2>
          <p>
            These Terms begin when Customer first accepts them, accesses the Services, or enters
            into an Order, and continue until terminated.
          </p>
          <p>Subscription terms are stated in the applicable Order.</p>
          <p>
            Either party may terminate for material breach if the breach is not cured within 30
            days after written notice.
          </p>
          <p>
            Boo Lab may suspend or terminate access immediately if required by law, if continued
            access creates a security risk, or if Customer materially violates the Acceptable Use
            section.
          </p>
          <p>
            Upon termination, Customer's right to use the Services ends. Boo Lab may delete
            Customer Content after termination according to its retention practices and
            applicable law.
          </p>
          <p>
            Sections that by their nature should survive will survive, including
            confidentiality, payment obligations, intellectual property, disclaimers, limitations
            of liability, indemnity, governing law, dispute resolution, and general provisions.
          </p>

          <h2 className="mt-10 text-lg font-semibold">20. Disclaimers</h2>
          <p>
            To the fullest extent permitted by law, the Services are provided as is and as
            available.
          </p>
          <p>
            Boo Lab disclaims all warranties, express, implied, statutory, or otherwise,
            including warranties of merchantability, fitness for a particular purpose, title,
            non infringement, accuracy, availability, security, and error free operation.
          </p>
          <p>
            Boo Lab does not warrant that the Services will be uninterrupted, secure, accurate,
            compliant with Customer's specific legal obligations, or suitable for any particular
            use case.
          </p>

          <h2 className="mt-10 text-lg font-semibold">21. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, neither party will be liable for indirect,
            incidental, special, consequential, exemplary, or punitive damages, or for lost
            profits, lost revenue, lost goodwill, lost data, business interruption, or cost of
            substitute services, even if advised of the possibility of such damages.
          </p>
          <p>
            To the fullest extent permitted by law, each party's total liability arising out of
            or relating to these Terms will not exceed the amounts paid or payable by Customer to
            Boo Lab for the Services during the 12 months before the event giving rise to
            liability.
          </p>
          <p>
            The above cap does not apply to Customer's payment obligations, misuse of Boo Lab
            intellectual property, or liability that cannot be limited by law.
          </p>

          <h2 className="mt-10 text-lg font-semibold">22. Indemnity</h2>
          <p>
            Customer will defend, indemnify, and hold harmless Boo Lab from and against claims,
            damages, liabilities, losses, costs, and expenses, including reasonable attorneys'
            fees, arising from Customer Content, Customer's use of the Services, Customer's
            integrations, Customer's violation of law, Customer's breach of these Terms, or
            disputes between Customer and its Users or third parties.
          </p>
          <p>
            Boo Lab will defend Customer against third party claims alleging that the Services,
            when used as authorized, infringe that third party's intellectual property rights,
            and will pay damages finally awarded or amounts agreed in settlement, provided
            Customer promptly notifies Boo Lab, gives Boo Lab control of the defense and
            settlement, and reasonably cooperates.
          </p>
          <p>
            Boo Lab has no obligation for claims arising from Customer Content, Third Party
            Services, Customer modifications, unauthorized use, use after Boo Lab provides a
            workaround or notice to stop, or combination with items not provided by Boo Lab.
          </p>
          <p>
            If the Services are or may be subject to an infringement claim, Boo Lab may procure
            the right to continue using them, modify them, replace them, or terminate the
            affected Services and provide a prorated refund of prepaid unused fees.
          </p>

          <h2 className="mt-10 text-lg font-semibold">23. Publicity</h2>
          <p>
            Boo Lab may identify Customer as a customer and use Customer's name and logo in
            customer lists, pitch materials, and marketing materials unless Customer notifies Boo
            Lab at legal@boolab.ai that it opts out.
          </p>

          <h2 className="mt-10 text-lg font-semibold">24. Notices</h2>
          <p>Legal notices to Boo Lab must be sent to legal@boolab.ai and to:</p>
          <p>
            Boo Lab, Inc.
            <br />
            1515 E Cesar Chavez St, Suite 100
            <br />
            Austin, TX 78702
            <br />
            USA
          </p>
          <p>
            Notices to Customer may be sent to the email address associated with Customer's
            account, through the Services, or as stated in an Order.
          </p>

          <h2 className="mt-10 text-lg font-semibold">25. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, without regard to
            conflict of law rules.
          </p>

          <h2 className="mt-10 text-lg font-semibold">26. Dispute Resolution</h2>
          <p>
            The parties will first try to resolve any dispute through good faith discussions
            between authorized representatives.
          </p>
          <p>
            If a dispute cannot be resolved informally, either party may pursue any remedy
            available under applicable law in the state or federal courts located in Delaware,
            unless the parties agree in writing to another dispute resolution process.
          </p>
          <p>Each party consents to the personal jurisdiction and venue of those courts.</p>
          <p>
            Nothing in this section prevents either party from seeking injunctive or equitable
            relief to protect confidential information, intellectual property, data, systems,
            security, or access to the Services.
          </p>

          <h2 className="mt-10 text-lg font-semibold">27. Changes to These Terms</h2>
          <p>
            Boo Lab may update these Terms from time to time. If Boo Lab makes material changes,
            Boo Lab will provide reasonable notice, which may include posting updated Terms at
            https://boolab.ai, sending an email, or providing notice through the Services.
          </p>
          <p>
            Updated Terms will apply prospectively unless otherwise required by law or agreed by
            Customer.
          </p>

          <h2 className="mt-10 text-lg font-semibold">28. Assignment</h2>
          <p>
            Customer may not assign these Terms without Boo Lab's prior written consent, except
            to an affiliate or in connection with a merger, reorganization, sale of substantially
            all assets, or change of control, provided the assignee assumes all obligations under
            these Terms.
          </p>
          <p>
            Boo Lab may assign these Terms to an affiliate or in connection with a merger,
            reorganization, sale of assets, financing, or change of control.
          </p>

          <h2 className="mt-10 text-lg font-semibold">29. Force Majeure</h2>
          <p>
            Neither party is liable for delay or failure to perform due to events beyond its
            reasonable control, including natural disasters, war, terrorism, labor disputes,
            internet or telecommunications failures, cloud provider outages, third party service
            failures, government actions, or power failures.
          </p>

          <h2 className="mt-10 text-lg font-semibold">30. General</h2>
          <p>
            These Terms, together with any Orders and policies incorporated by reference, are
            the entire agreement between the parties regarding the Services.
          </p>
          <p>If any provision is unenforceable, the remaining provisions will remain in effect.</p>
          <p>Failure to enforce a provision is not a waiver.</p>
          <p>There are no third party beneficiaries unless expressly stated.</p>
          <p>Headings are for convenience only.</p>

          <h2 className="mt-10 text-lg font-semibold">31. Contact</h2>
          <p>
            Boo Lab, Inc.
            <br />
            1515 E Cesar Chavez St, Suite 100
            <br />
            Austin, TX 78702
            <br />
            USA
          </p>
          <p>
            Legal notices:{" "}
            <a
              href="mailto:legal@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              legal@boolab.ai
            </a>
            <br />
            Privacy contact:{" "}
            <a
              href="mailto:privacy@boolab.ai"
              className="underline underline-offset-4 hover:text-foreground"
            >
              privacy@boolab.ai
            </a>
          </p>
        </div>

        <footer className="mt-12 flex gap-4 border-t pt-6 text-sm text-muted-foreground">
          <Link
            to="/privacy-policy"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Privacy Policy
          </Link>
          <Link to="/login" className="underline underline-offset-4 hover:text-foreground">
            Sign in
          </Link>
        </footer>
      </div>
    </div>
  );
}
